// OnshapeBomPanel — the Prepare → Load BOM panel, extracted out of
// OnshapeView.jsx's OnshapeDetailPanel (Spike_OnshapePanelMode_Brief.md) so the
// budget/cap/Prepare rule lives in EXACTLY ONE place, shared by both the
// Onshape bucket (OnshapeView.jsx) and panel mode (OnshapePanel.jsx). Do not
// fork this logic — extend it here if either caller needs more.
//
// HARD RULE (Alex): an Onshape call only fires from a user action that already
// KNOWS the cost. So this is two-step:
//   "Prepare"  → GET ?ref=            → the PLAN (structure only, 1-2 cached
//                                       calls): how many parts / how many calls.
//   "Load (N)" → GET ?confirm=1&budget=N → executes, capped at the N the user
//                                       just saw. Server refuses without it.
//
// Props: item — { code, onshape_ref } (code only used to reset state when the
// selected item changes; onshape_ref drives the fetches). Renders nothing of
// its own chrome (no header/card) — callers wrap it in whatever card/header
// idiom fits their layout.

import { useState, useEffect, useCallback } from 'react';

import { BTN_PRIMARY, BTN_PRIMARY_OUTLINE, BTN_DISABLED } from '../theme-tokens.js';

// Identity display — whichever of product_purch/product_shop/product_site
// is set (labeled), or a muted "no identity set" when all three are null.
function identityLabel(identity) {
  if (!identity) return null;
  if (identity.product_purch) return { label: 'purch', value: identity.product_purch };
  if (identity.product_shop) return { label: 'shop', value: identity.product_shop };
  if (identity.product_site) return { label: 'site', value: identity.product_site };
  return null;
}

export default function OnshapeBomPanel({ item }) {
  const [bomStatus, setBomStatus] = useState('idle'); // idle | planning | planned | loading | loaded | error
  const [plan, setPlan] = useState(null);       // { kind, parts, identityCalls, cap, overCap }
  const [takeoff, setTakeoff] = useState(null); // { kind, parts: [...] }
  const [bomError, setBomError] = useState(null);

  // Selecting a different item drops any previously loaded takeoff — stays
  // idle until "Load BOM" is clicked again for the new item.
  useEffect(() => {
    setBomStatus('idle');
    setPlan(null);
    setTakeoff(null);
    setBomError(null);
  }, [item?.onshape_ref, item?.code]);

  const prepare = useCallback(() => {
    if (!item) return;
    setBomStatus('planning');
    setBomError(null);
    fetch(`/api/onshape/takeoff?ref=${encodeURIComponent(item.onshape_ref || '')}`)
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      .then(data => { setPlan(data.plan); setBomStatus('planned'); })
      .catch(err => { setBomError(err.message); setBomStatus('error'); });
  }, [item]);

  const loadBom = useCallback(() => {
    if (!item || !plan) return;
    setBomStatus('loading');
    setBomError(null);
    const q = `ref=${encodeURIComponent(item.onshape_ref || '')}&confirm=1&budget=${plan.identityCalls}`;
    fetch(`/api/onshape/takeoff?${q}`)
      .then(res => res.json().then(b => { if (!res.ok) throw new Error(b.error || `HTTP ${res.status}`); return b; }))
      .then(data => { setTakeoff(data); setBomStatus('loaded'); })
      .catch(err => { setBomError(err.message); setBomStatus('error'); });
  }, [item, plan]);

  if (!item) return null;

  return (
    <>
      {/* Step 1 — Prepare: costs only the 1-2 (cached) structure calls and
          tells the user exactly what a full load would spend. */}
      <button
        onClick={prepare}
        disabled={bomStatus === 'planning' || bomStatus === 'loading'}
        style={{
          ...BTN_PRIMARY_OUTLINE, display: 'block', marginTop: 12, width: '100%', textAlign: 'center',
          ...((bomStatus === 'planning' || bomStatus === 'loading') ? BTN_DISABLED : null),
        }}
      >
        {bomStatus === 'planning' ? 'Checking…' : 'Prepare (check cost)'}
      </button>

      {/* Step 2 — the cost, then an explicit Load capped at that number. */}
      {plan && (
        <div style={{ marginTop: 8 }}>
          {/* The part count is no longer shown here, because it is no longer
              knowable without doing the read — and the read IS the whole job now
              (bulk: 1 call for a part studio, 2 for an assembly). Cost stopped
              scaling with part count on 2026-07-15, so there is nothing left for
              Prepare to go and measure. It reports the ceiling instead. */}
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-body)' }}>
            Bulk read · at most <strong>{plan.identityCalls}</strong> Onshape call{plan.identityCalls === 1 ? '' : 's'}
            {' '}<span style={{ color: 'var(--th-text-hint)' }}>(part studio 1 · assembly 2)</span>
          </div>
          {plan.overCap ? (
            <div style={{ marginTop: 4, color: 'var(--th-status-deprecated)', fontSize: 'var(--fs-small)' }}>
              ⚠ Over the {plan.cap}-call cap — refused. Needs a batch read first.
            </div>
          ) : (
            <button
              onClick={loadBom}
              disabled={bomStatus === 'loading'}
              style={{
                ...BTN_PRIMARY, display: 'block', marginTop: 4, width: '100%', textAlign: 'center',
                ...(bomStatus === 'loading' ? BTN_DISABLED : null),
              }}
            >
              {bomStatus === 'loading' ? 'Loading…' : `Load — ${plan.identityCalls} call${plan.identityCalls === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      )}

      {bomStatus === 'error' && (
        <div style={{ marginTop: 8, color: 'var(--th-status-deprecated)', fontSize: 'var(--fs-small)' }}>⚠ {bomError}</div>
      )}

      {bomStatus === 'loaded' && (
        <>
          <div style={{ marginTop: 12, fontSize: 'var(--fs-small)', fontWeight: 700, color: 'var(--th-text-body)' }}>
            Takeoff — {takeoff?.kind === 'assembly' ? 'assembly' : 'part studio'}
          </div>

          {/* Part · Qty · Category · Identity · Mass — real Onshape takeoff, one row per part. */}
          {takeoff?.parts && takeoff.parts.length > 0 ? (
            <table style={{ marginTop: 4, width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-small)' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--th-border-subtle)', color: 'var(--th-text-secondary)' }}>Part</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid var(--th-border-subtle)', color: 'var(--th-text-secondary)' }}>Qty</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--th-border-subtle)', color: 'var(--th-text-secondary)' }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--th-border-subtle)', color: 'var(--th-text-secondary)' }}>Identity</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px', borderBottom: '1px solid var(--th-border-subtle)', color: 'var(--th-text-secondary)' }}>Mass</th>
                </tr>
              </thead>
              <tbody>
                {takeoff.parts.map((row, i) => {
                  const id = identityLabel(row.identity);
                  return (
                    <tr key={`${row.partId}-${i}`} style={{ background: i % 2 === 0 ? 'var(--th-panel-bg)' : 'var(--th-row-even)' }}>
                      <td style={{ padding: '4px 8px', color: 'var(--th-text-body)' }}>{row.name}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--th-text-body)' }}>{row.qty}</td>
                      <td style={{ padding: '4px 8px', color: row.category ? 'var(--th-text-body)' : 'var(--th-text-hint)' }}>{row.category || '—'}</td>
                      <td style={{ padding: '4px 8px', color: id ? 'var(--th-text-body)' : 'var(--th-text-hint)' }}>
                        {id ? <><span style={{ color: 'var(--th-text-secondary)' }}>{id.label}:</span> {id.value}</> : '— no identity set'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: row.mass != null ? 'var(--th-text-body)' : 'var(--th-text-hint)' }}>
                        {row.mass != null ? `${row.mass.toFixed(1)} kg` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ marginTop: 4, color: 'var(--th-text-hint)', fontSize: 'var(--fs-small)' }}>No parts found.</div>
          )}

          <div style={{
            marginTop: 12, padding: '8px 10px', border: '1px solid var(--th-border-subtle)',
            borderRadius: 4, background: 'var(--th-row-even)', color: 'var(--th-text-hint)',
            fontSize: 'var(--fs-small)',
          }}>
            price — coming (slice 3b)
          </div>
        </>
      )}
    </>
  );
}
